import { Injectable, signal } from '@angular/core';

import { adminGetOnboarding, adminMarkOnboarded } from './callables';

/**
 * Whether this admin has finished the welcome tour, per the server.
 *
 * The flag lives on their `blumeStaff/{uid}` doc rather than in this browser,
 * so a second machine does not replay a tour that explains what roles they
 * hold. That collection is `if false` for clients in firestore.rules and stays
 * that way - both calls here go through Callables that read the uid off the
 * verified token, so neither takes a target and neither needed the registry
 * opened up.
 *
 * ## What happens when the calls fail
 *
 * A failed `load()` leaves `loaded` false, which keeps the tour SHUT. That is
 * the deliberate direction to fail in: an admin who misses the tour can replay
 * it from Settings, whereas one shown it on every load because the fetch keeps
 * failing has no way to make it stop.
 *
 * A failed `complete()` still marks it done locally, so the dialog they just
 * dismissed does not reopen in this session. It will come back on the next
 * load, which is the honest outcome - the server genuinely does not know.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingStore {
  private readonly _onboardedAt = signal<number | null>(null);
  private readonly _loaded = signal(false);

  /** When they finished the tour, or null if they have not. Only meaningful
   *  once {@link loaded} is true - before that, null means "not asked yet". */
  readonly onboardedAt = this._onboardedAt.asReadonly();

  /** Has the server actually answered? */
  readonly loaded = this._loaded.asReadonly();

  /** Guards against a second fetch: the shell's effect can re-run for reasons
   *  that have nothing to do with this (a role refresh, the boot gate lifting),
   *  and this is a network call per run otherwise. */
  private inFlight: Promise<void> | null = null;

  async load(): Promise<void> {
    if (this._loaded()) return;
    if (this.inFlight) return this.inFlight;
    this.inFlight = (async () => {
      try {
        const result = await adminGetOnboarding({});
        this._onboardedAt.set(result.onboardedAt);
        this._loaded.set(true);
      } catch {
        // Stay unloaded - see the header. Not surfaced to the reader: nothing
        // they can do, and a toast about a welcome tour on top of whatever
        // else is failing is noise.
      } finally {
        this.inFlight = null;
      }
    })();
    return this.inFlight;
  }

  /** Record the tour as finished. Called from ONE place - the last step's
   *  button - because that is the only thing that finishes it. Still
   *  idempotent, because replaying from Settings runs the same page and calls
   *  this again on the way out; the server keeps the first stamp, so the date
   *  someone actually joined is never rewritten. */
  async complete(): Promise<void> {
    if (this._onboardedAt() !== null) return; // already done; no call to make
    this._onboardedAt.set(Date.now()); // optimistic, so the guard lets them out
    try {
      const result = await adminMarkOnboarded({});
      this._onboardedAt.set(result.onboardedAt); // the server's stamp wins
    } catch {
      // Local state stands for this session; the next load asks again.
    }
  }
}
